import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    canSubmitForQuote,
    getCartState,
    getCurrentCartState,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import placeOrderForApproval from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/PlaceOrderForApproval";
import setIsPlacingOrder from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetIsPlacingOrder";
// import setOnePageCheckoutPaymentMethod from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetOnePageCheckoutPaymentMethod";
import Zone from "@insite/client-framework/Components/Zone";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import updateCurrentAccount from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/UpdateCurrentAccount";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@sunSource/Store/CustomApplicationState";
import { customIsCartCheckoutDisabled } from "@sunSource/Store/Data/Carts/CustomCartsSelector";
import setOnePageCheckoutPaymentMethod from "@sunSource/Store/Data/NewOnePageCheckout/Handlers/SetOnePageCheckoutPaymentMethod";
import { getOnePageCheckoutCustomNotesState } from "@sunSource/Store/Data/OnePageCheckoutCustomNotes/OnePageCheckoutCustomNotesSelector";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    termsAndConditionsLink = "termsAndConditionsLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.termsAndConditionsLink]: LinkFieldValue;
    };
}

const mapStateToProps = (state: CustomApplicationState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart, isLoading } = cartState;
    const promotions = getCurrentPromotionsDataView(state).value;
    const isPyamentMethodName = getOnePageCheckoutCustomNotesState(state);
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;
    return {
        cart,
        account: getCurrentAccountState(state).value,
        billTo: getBillToState(state, cart?.billToId).value,
        orderPromotions: promotions ? getOrderPromotions(promotions) : undefined,
        shippingPromotions: promotions ? getShippingPromotions(promotions) : undefined,
        discountTotal: promotions ? getDiscountTotal(promotions) : undefined,
        isLoading,
        isExpanded: state.pages.onePageCheckout.paymentDetailsState === "complete" || canSubmitForApprovalOrder(cart),
        isPlacingOrder: state.pages.onePageCheckout.isPlacingOrder,
        isPyamentMethodName: isPyamentMethodName?.isPaymentMethodName ? isPyamentMethodName.isPaymentMethodName : "",
        placeOrderErrorMessage: state.pages.onePageCheckout.placeOrderErrorMessage,
        showPlaceOrderButton: canPlaceOrder(cart),
        showSubmitForApprovalOrderButton: canSubmitForApprovalOrder(cart),
        orderApprovalDetailsPageUrl: getPageLinkByPageType(state, "OrderApprovalDetailsPage")?.url,
        isCartCheckoutDisabled: customIsCartCheckoutDisabled(cart),
        canSubmitForQuote: canSubmitForQuote(cart),
        isSubmitForQuoteButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitRequisitionButtonDisabled: isCartLoading || isUpdatingCart,
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        isAuthenticated: state?.context?.session?.isAuthenticated,
    };
};

const mapDispatchToProps = {
    setIsPlacingOrder,
    placeOrderForApproval,
    setOnePageCheckoutPaymentMethod,
    updateCurrentAccount,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OnePageCheckoutReviewAndPlaceOrderStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    cartTotalGridItem?: GridItemProps;
    cartTotal?: CartTotalDisplayStyles;
    checkboxesGridItem?: GridItemProps;
    signUpForNewslettersCheckbox?: CheckboxPresentationProps;
    placeOrderGridItem?: GridItemProps;
    placeOrderButton?: ButtonPresentationProps;
    agreementText?: TypographyPresentationProps;
    placeOrderErrorMessageGridItem?: GridItemProps;
    placeOrderErrorMessageText?: TypographyPresentationProps;
    submitForApprovalButton?: ButtonPresentationProps;
    warningIcon?: IconPresentationProps;
    warningSection?: InjectableCss;
    warningText?: InjectableCss;
    submitQuoteButton?: ButtonPresentationProps;
    requestForQuoteModal?: ModalPresentationProps;
}

export const reviewAndPlaceOrderStyles: OnePageCheckoutReviewAndPlaceOrderStyles = {
    container: {
        gap: 20,
        css: css`
            padding-top: 30px;
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    cartTotalGridItem: { width: 12 },
    cartTotal: {
        container: {
            gap: 10,
            css: css`
                padding: 0;
                background-color: transparent;
            `,
        },
    },
    checkboxesGridItem: { width: 12 },
    placeOrderGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            gap: 12px;
        `,
    },
    placeOrderButton: {
        variant: "tertiary",
        css: css`
            width: 100%;
            text-transform: uppercase;
            font-weight: 400;
        `,
    },
    agreementText: {
        size: 13,
        css: css`
            margin-top: 8px;
        `,
    },
    placeOrderErrorMessageGridItem: { width: 12 },
    placeOrderErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            margin-bottom: 15px;
        `,
    },
    submitForApprovalButton: {
        variant: "primary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    warningIcon: {
        src: "AlertCircle",
        color: "danger.main",
        css: css`
            margin-right: 10px;
        `,
    },
    warningSection: {
        css: css`
            display: flex;
            align-items: center;
            border: 2px solid ${getColor("danger.main")};
            padding: 15px 10px;
        `,
    },
    submitQuoteButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
            text-transform: uppercase;
        `,
    },
    requestForQuoteModal: {
        size: 650,
        cssOverrides: {
            modalBody: css`
                .RFQ-container {
                    background: rgba(225, 221, 226, 0.3);
                }
            `,
            modalTitle: css`
                padding: 0px;
                border: none;
                > ${TypographyStyle} {
                    color: ${getColor("secondary")};
                    font-size: 1rem;
                }
                + [class^="ModalContent-sc-"] {
                    padding: 10px 30px;
                }
                #CloseModalButton {
                    position: absolute;
                    height: 33px;
                    top: 25px;
                    right: 25px;
                    width: 33px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: #ffffff !important;
                    border: none !important;
                    min-height: 33px;
                    > span {
                        color: #275aa8;
                        position: unset;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                    }
                    svg {
                        height: 21px;
                        width: 21px;
                    }
                }
            `,
        },
    },
};

const styles = reviewAndPlaceOrderStyles;

const OnePageCheckoutReviewAndPlaceOrder = ({
    fields,
    cart,
    account,
    billTo,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    isExpanded,
    isPlacingOrder,
    placeOrderErrorMessage,
    showPlaceOrderButton,
    showSubmitForApprovalOrderButton,
    orderApprovalDetailsPageUrl,
    history,
    isCartCheckoutDisabled,
    setIsPlacingOrder,
    placeOrderForApproval,
    updateCurrentAccount,
    setOnePageCheckoutPaymentMethod,
    isPyamentMethodName,
    canSubmitForQuote,
    isSubmitForQuoteButtonDisabled,
    hasQuoteRequiredProducts,
    isSubmitRequisitionButtonDisabled,
    id,
    isAuthenticated,
}: Props) => {
    const [signUpForNewsletters, setSignUpForNewslettersCheckbox] = useState(false);
    const { url: termsAndConditionsUrl } = useGetLink(fields.termsAndConditionsLink);

    if (!cart) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner data-test-selector="onePageCheckout_reviewAndPlaceOrder" />
            </StyledWrapper>
        );
    }
    const cartLines = cart.cartLines || [];
    const quoteRequiredCartLines = cartLines.filter(
        cartLine =>
            cartLine.quoteRequired ||
            (!cartLine.quoteRequired &&
                !cartLine.allowZeroPricing &&
                cartLine.pricing &&
                getUnitNetPrice(cartLine.pricing, 1).price === 0),
    );
    const purchaseCartLines = cartLines.filter(
        cartLine =>
            !cartLine.quoteRequired &&
            (cartLine.allowZeroPricing || (cartLine.pricing && getUnitNetPrice(cartLine.pricing, 1).price !== 0)),
    );

    const paymentOptions = cart ? cart.paymentOptions : undefined;
    const paymentMethods = paymentOptions ? paymentOptions.paymentMethods : undefined;
    const emailForSubsribe = account?.isGuest ? billTo?.email : account?.email;

    const placeOrderClickHandler = () => {
        // setOnePageCheckoutPaymentMethod({
        //     paymentMethod: paymentMethods?.find(method => method.name === isPyamentMethodName),
        // });
        if (account && account.isSubscribed === false && signUpForNewsletters && emailForSubsribe) {
            updateCurrentAccount({
                account: { ...account, isSubscribed: true, email: emailForSubsribe },
            });
        }
        setIsPlacingOrder({ isPlacingOrder: true });
    };

    const submitForApprovalOrderClickHandler = () => {
        placeOrderForApproval({
            onSuccess: (cartId: string) => {
                if (orderApprovalDetailsPageUrl) {
                    history.push(`${orderApprovalDetailsPageUrl}?cartId=${cartId}`);
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.cart.id) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult.cart.id);
                }
            },
        });
    };

    // const submitForQuoteLabel = cart?.isSalesperson ? translate("Create a Quote") : translate("Submit for Quote");

    const placeOrderClickHandlerCustom = () => {
        placeOrderClickHandler();
    };

    return (
        <GridContainer {...styles.container} data-test-selector="onePageCheckoutReviewAndPlaceOrder_header">
            {!isExpanded && (
                <>
                    <Button {...styles.placeOrderButton} disabled={true}>
                        {translate(
                            quoteRequiredCartLines.length > 0
                                ? purchaseCartLines.length > 0
                                    ? "Place Order and submit quote request"
                                    : "Submit quote request"
                                : "Place Order",
                        )}
                    </Button>
                </>
            )}
            {isExpanded && (
                <>
                    <GridItem {...styles.cartTotalGridItem}>
                        <CartTotalDisplay
                            cart={cart}
                            orderPromotions={orderPromotions}
                            shippingPromotions={shippingPromotions}
                            discountTotal={discountTotal}
                            extendedStyles={styles.cartTotal}
                        />
                    </GridItem>
                    {account && account.isSubscribed === false && emailForSubsribe && (
                        <GridItem {...styles.checkboxesGridItem}>
                            <Checkbox
                                {...styles.signUpForNewslettersCheckbox}
                                onChange={(_, value) => setSignUpForNewslettersCheckbox(value)}
                                checked={signUpForNewsletters}
                                data-test-selector="checkoutReviewAndSubmit_signUpForNewsletters"
                            >
                                {siteMessage("SignIn_Sign_Up_Newsletter")}
                            </Checkbox>
                        </GridItem>
                    )}
                    <GridItem {...styles.placeOrderGridItem}>
                        {showPlaceOrderButton && (
                            <Button
                                {...styles.placeOrderButton}
                                disabled={isLoading || isPlacingOrder || isCartCheckoutDisabled}
                                onClick={() => placeOrderClickHandlerCustom()}
                                data-test-selector="checkoutReviewAndSubmit_placeOrder"
                            >
                                {translate(
                                    quoteRequiredCartLines.length > 0
                                        ? purchaseCartLines.length > 0
                                            ? "Place Order and submit quote request"
                                            : "Submit quote request"
                                        : "Place Order",
                                )}
                            </Button>
                        )}
                        {showPlaceOrderButton && isCartCheckoutDisabled && (
                            <StyledWrapper {...styles.warningSection}>
                                <Icon {...styles.warningIcon}></Icon>
                                <Typography
                                    {...styles.warningText}
                                    data-test-selector="cartlineHeader_NoPriceAvailableAtCheckout"
                                >
                                    {siteMessage("Cart_InvalidPriceAtCheckout")}
                                </Typography>
                            </StyledWrapper>
                        )}
                        {showSubmitForApprovalOrderButton && (
                            <Button
                                {...styles.submitForApprovalButton}
                                disabled={isLoading || isPlacingOrder}
                                onClick={submitForApprovalOrderClickHandler}
                                data-test-selector="checkoutReviewAndSubmit_submitForApproval"
                            >
                                {translate("Submit For Approval")}
                            </Button>
                        )}
                        {showPlaceOrderButton && termsAndConditionsUrl && (
                            <Typography {...styles.agreementText}>
                                {siteMessage("Checkout_Agreement", termsAndConditionsUrl)}
                            </Typography>
                        )}
                    </GridItem>
                    {placeOrderErrorMessage && (
                        <GridItem {...styles.placeOrderErrorMessageGridItem}>
                            <Typography {...styles.placeOrderErrorMessageText}>{placeOrderErrorMessage}</Typography>
                        </GridItem>
                    )}
                </>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OnePageCheckoutReviewAndPlaceOrder)),
    definition: {
        group: "One Page Checkout",
        // allowedContexts: ["OnePageCheckoutPage"],
        displayName: "New onepage checkout Review & Place Order",
        fieldDefinitions: [
            {
                name: fields.termsAndConditionsLink,
                fieldType: "Contextual",
                displayName: "Terms And Conditions Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};

export default widgetModule;
